import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";
import { PreviewCard } from "./ResumeVersions";

export default function TailorResume() {
  const [resumes, setResumes] = useState([]);
  const [form, setForm] = useState({ resume_id: "", job_title: "", company: "", job_description: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/resumes").then((r) => {
      setResumes(r.data || []);
      if (r.data?.[0]) setForm((f) => ({ ...f, resume_id: r.data[0].resume_id }));
    });
  }, []);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!form.resume_id) return toast.error("Choose a resume");
    if (!form.job_title || !form.company || !form.job_description) return toast.error("Fill all fields");
    setBusy(true);
    try {
      const r = await api.post("/tailor", form);
      setData(r.data);
      toast.success("Tailored package ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const ms = data?.match_score;
  const tone = ms == null ? "#94a3b8" : ms >= 70 ? "#10B981" : ms >= 40 ? "#F59E0B" : "#EF4444";

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Tailor</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">Tailor your resume to the job</h1>
        <p className="text-slate-400 mt-2 max-w-xl">
          We surface keywords, rewrite bullets for relevance, and draft a matching cover letter — using only your real history.
        </p>
      </div>

      <form onSubmit={run} className="glass p-6 fade-up-delay-1 grid lg:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Resume</span>
          <select
            data-testid="tailor-resume"
            value={form.resume_id}
            onChange={(e) => setForm({ ...form, resume_id: e.target.value })}
            className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3 py-2.5 text-sm bg-white"
          >
            <option value="">— Select —</option>
            {resumes.map((r) => <option key={r.resume_id} value={r.resume_id}>{r.filename}</option>)}
          </select>
        </label>
        <Input label="Job title" value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} testid="tailor-title" />
        <Input label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="tailor-company" />
        <div className="lg:col-span-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Job description</span>
          <textarea
            data-testid="tailor-jd"
            value={form.job_description}
            onChange={(e) => setForm({ ...form, job_description: e.target.value })}
            rows={8}
            className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-3 text-sm font-mono"
            placeholder="Paste the full JD here…"
          />
        </div>
        <div className="lg:col-span-2 flex justify-end">
          <button
            data-testid="tailor-run"
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-[#1E6FE6] hover:bg-[#1257C4] text-white px-6 py-3 font-bold text-sm shadow-lg shadow-indigo-500/20 disabled:opacity-60"
          >
            <Wand2 size={16} /> {busy ? "Tailoring…" : "Tailor resume"}
          </button>
        </div>
      </form>

      {data && (
        <div className="space-y-6 fade-up-delay-2">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="glass p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Match score</div>
              <div className="font-display font-black text-5xl mt-2 leading-none" style={{ color: tone }}>
                {ms}<span className="text-xl">%</span>
              </div>
            </div>
            <div className="glass p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-[#80FFEA] mb-2">Matching keywords</div>
              <div className="flex flex-wrap gap-2">
                {(data.matching_keywords || []).slice(0, 12).map((k) => (
                  <span key={k} className="px-2.5 py-1 rounded-full bg-[rgba(128,255,234,0.1)] text-[#80FFEA] text-xs font-semibold">{k}</span>
                ))}
                {!data.matching_keywords?.length && <span className="text-slate-400 text-sm">—</span>}
              </div>
            </div>
            <div className="glass p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-[#FF6DAE] mb-2">Missing keywords</div>
              <div className="flex flex-wrap gap-2">
                {(data.missing_keywords || []).slice(0, 12).map((k) => (
                  <span key={k} className="px-2.5 py-1 rounded-full bg-[rgba(255,0,127,0.1)] text-[#FF6DAE] text-xs font-semibold">{k}</span>
                ))}
                {!data.missing_keywords?.length && <span className="text-slate-400 text-sm">—</span>}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <PreviewCard testid="tailored-resume" title="Tailored Resume" md={data.tailored_resume_markdown || ""} filename={`cavilo-tailored-${form.company || "resume"}`} />
            <PreviewCard testid="tailored-cover" title="Tailored Cover Letter" md={data.tailored_cover_letter_markdown || ""} filename={`cavilo-tailored-${form.company || "cover"}`} />
          </div>

          <div className="glass p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Interview prep</div>
            <div className="font-display font-bold text-xl mt-1">Top 5 questions for this role</div>
            <ol className="mt-4 space-y-2 list-decimal list-inside text-sm text-slate-200">
              {(data.interview_questions || []).map((q, i) => <li key={i}>{q}</li>)}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm"
      />
    </label>
  );
}
